import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Section } from "../../components/vc/layout";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeft, Building2, Phone, Mail, MessageCircle, User, Users, Tag, Image as ImageIcon, X, Save,
} from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppUrl } from "../../lib/phone";

/**
 * #6 Business Portfolio
 *  - /portfolio          → the current user's editable portfolio
 *  - /portfolio/:ownerId → read-only portfolio of a connected business
 */
export default function VCBusinessPortfolio() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const editable = !ownerId; // no ownerId => "my" portfolio, editable
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "", description: "", owner_name: "", partner_name: "",
    phone: "", email: "", categories: [], images: [],
  });
  const [categoryInput, setCategoryInput] = useState("");

  useEffect(() => {
    const url = editable ? "/me/portfolio" : `/portfolios/${ownerId}`;
    apiClient.get(url)
      .then((r) => {
        setForm({
          company_name: r.data.company_name || "",
          description: r.data.description || "",
          owner_name: r.data.owner_name || "",
          partner_name: r.data.partner_name || "",
          phone: r.data.phone || "",
          email: r.data.email || "",
          categories: r.data.categories || [],
          images: r.data.images || [],
        });
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Portfolio not found");
        navigate("/businesses");
      })
      .finally(() => setLoading(false));
  }, [ownerId]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target?.value ?? e }));

  const addCategory = () => {
    const v = categoryInput.trim();
    if (!v) return;
    if (form.categories.includes(v)) { toast.info("Already added"); return; }
    setForm((f) => ({ ...f, categories: [...f.categories, v] }));
    setCategoryInput("");
  };
  const removeCategory = (v) => setForm((f) => ({ ...f, categories: f.categories.filter((c) => c !== v) }));

  const onFile = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (form.images.length >= 5) { toast.error("Maximum 5 images"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, images: [...f.images, ev.target.result] }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/me/portfolio", form);
      toast.success("Portfolio saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="px-4 pt-4 pb-28">
          <div className="h-40 bg-white rounded-3xl border border-slate-100 animate-pulse" />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4 max-w-3xl mx-auto lg:px-8">
        <button onClick={() => navigate(-1)} data-testid="portfolio-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <Building2 className="w-3 h-3" strokeWidth={2.6} /> Business Portfolio
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">
            {editable ? "Your business portfolio" : form.company_name || "Business"}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {editable ? "Add details, product categories, and up to 5 images so connected businesses can learn about you." : "Public details shared by this business"}
          </p>
        </div>

        {/* Basic details */}
        <Section title="Company details" subtitle="Public information about your business" testid="portfolio-details-section">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
            {/* 1. Company Name */}
            <FieldRow label="Company name" Icon={Building2}>
              {editable ? (
                <Input value={form.company_name} onChange={setF("company_name")} data-testid="portfolio-company-name" className="h-11 rounded-xl" />
              ) : <ReadValue value={form.company_name} />}
            </FieldRow>
            {/* 2. Owner Name / Partner Name */}
            <FieldRow label="Owner name" Icon={User}>
              {editable ? (
                <Input value={form.owner_name} onChange={setF("owner_name")} data-testid="portfolio-owner-name" className="h-11 rounded-xl" />
              ) : <ReadValue value={form.owner_name} />}
            </FieldRow>
            <FieldRow label="Partner name (optional)" Icon={Users}>
              {editable ? (
                <Input value={form.partner_name} onChange={setF("partner_name")} data-testid="portfolio-partner-name" className="h-11 rounded-xl" />
              ) : <ReadValue value={form.partner_name} />}
            </FieldRow>
            {/* 3. Company Description */}
            <FieldRow label="Description" Icon={Tag} multiline>
              {editable ? (
                <Textarea value={form.description} onChange={setF("description")} rows={3} placeholder="Short intro about what you sell / do" data-testid="portfolio-description" className="rounded-xl" />
              ) : <ReadValue value={form.description} multiline />}
            </FieldRow>
            {/* 4. Phone Number */}
            <FieldRow label="Phone" Icon={Phone}>
              {editable ? (
                <Input value={form.phone} onChange={setF("phone")} type="tel" data-testid="portfolio-phone" className="h-11 rounded-xl" />
              ) : <ReadValue value={form.phone} />}
            </FieldRow>
            {/* 5. Email ID */}
            <FieldRow label="Email" Icon={Mail}>
              {editable ? (
                <Input value={form.email} onChange={setF("email")} type="email" data-testid="portfolio-email" className="h-11 rounded-xl" />
              ) : <ReadValue value={form.email} />}
            </FieldRow>
          </div>
        </Section>

        {/* 6. Product Categories */}
        <Section title="Product categories" subtitle="Tags describing what this business deals with" testid="portfolio-categories-section">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
            {editable && (
              <div className="flex gap-2">
                <Input
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                  placeholder="Add a category and press Enter"
                  data-testid="portfolio-category-input"
                  className="h-11 rounded-xl"
                />
                <SecondaryButton onClick={addCategory} data-testid="portfolio-category-add">Add</SecondaryButton>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5" data-testid="portfolio-category-chips">
              {form.categories.length === 0 ? (
                <div className="text-xs text-slate-500">No categories yet.</div>
              ) : form.categories.map((c) => (
                <span key={c} data-testid={`portfolio-category-${c}`} className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                  {c}
                  {editable && (
                    <button onClick={() => removeCategory(c)} data-testid={`portfolio-category-remove-${c}`} className="w-5 h-5 rounded-full bg-emerald-600/20 hover:bg-emerald-600/30 grid place-items-center">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        </Section>

        {/* 7. Call / WhatsApp buttons (read-only view for connected businesses) */}
        {!editable && (form.phone) && (
          <div className="grid grid-cols-2 gap-2" data-testid="portfolio-contact-buttons">
            <SecondaryButton
              onClick={() => window.open(`tel:${form.phone}`)}
              data-testid="portfolio-call-owner"
              className="flex items-center justify-center gap-2 h-12"
            >
              <Phone className="w-4 h-4" /> Call owner
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                const url = buildWhatsAppUrl(form.phone);
                if (!url) { toast.error("This phone number looks invalid"); return; }
                window.open(url, "_blank");
              }}
              data-testid="portfolio-wa-owner"
              className="flex items-center justify-center gap-2 h-12"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp owner
            </PrimaryButton>
          </div>
        )}

        {/* 8. Company Images (max 5) — anchored to the bottom of the portfolio */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3" data-testid="portfolio-images">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-slate-500" />
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex-1">Company images ({form.images.length}/5)</div>
          </div>
          {form.images.length === 0 && !editable && (
            <div className="text-sm text-slate-500 text-center py-8">No images uploaded yet.</div>
          )}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {form.images.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-2xl overflow-hidden bg-slate-50 border border-slate-100" data-testid={`portfolio-image-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                {editable && (
                  <button
                    onClick={() => removeImage(i)}
                    data-testid={`portfolio-image-remove-${i}`}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {editable && form.images.length < 5 && (
              <label className="aspect-square rounded-2xl border-2 border-dashed border-slate-300 hover:border-emerald-400 grid place-items-center cursor-pointer transition" data-testid="portfolio-image-upload">
                <div className="text-center">
                  <ImageIcon className="w-6 h-6 text-slate-400 mx-auto" />
                  <div className="text-[10px] font-semibold text-slate-500 mt-1">Add image</div>
                </div>
                <input type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
            )}
          </div>
        </div>

        {editable && (
          <PrimaryButton
            onClick={save}
            loading={saving}
            data-testid="portfolio-save-btn"
            className="w-full h-14 flex items-center justify-center gap-2 text-base"
          >
            <Save className="w-5 h-5" /> Save Portfolio
          </PrimaryButton>
        )}
      </div>
    </>
  );
}

function FieldRow({ label, Icon, children, multiline }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={multiline ? "" : ""}>{children}</div>
    </div>
  );
}

function ReadValue({ value, multiline }) {
  const v = (value || "").trim();
  if (!v) return <div className="text-sm text-slate-400">—</div>;
  return <div className={`text-sm text-slate-800 ${multiline ? "whitespace-pre-wrap" : ""}`}>{v}</div>;
}
